import React, { useRef, useState } from 'react';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * AP35 — profil fotoğrafı yükleme dairesi (öğrenci / öğretmen / veli Profil
 * sayfaları). Mevcut avatar'ı gösterir; sağ-alt köşedeki kamera rozetine
 * tıklanınca dosya seçilir, anında /auth/me/avatar'a yüklenir ve auth context
 * yenilenir (avatarUrl tüm sayfalara yansır). 5MB / JPEG-PNG-WebP sınırı
 * backend ile aynıdır.
 */
const AvatarUpload: React.FC<{ size?: number }> = ({ size = 112 }) => {
  const { user, refresh } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const badge = Math.max(28, Math.round(size * 0.3));

  const pick = async (f: File | null) => {
    setError('');
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('Yalnızca JPEG, PNG veya WebP formatında görsel yükleyebilirsiniz');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('Fotoğraf 5MB sınırını aşıyor');
      return;
    }
    setBusy(true);
    try {
      await authApi.uploadAvatar(f);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Fotoğraf yüklenemedi — lütfen tekrar deneyin');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
      <div className="relative" style={{ width: size, height: size }}>
        <div className="w-full h-full rounded-full overflow-hidden" style={{ background: '#3973bf' }}>
          <img src={user?.avatarUrl || '/a-avatar.png'} alt="" className="w-full h-full object-cover" />
          {busy && (
            <div className="absolute inset-0 rounded-full bg-black/45 flex items-center justify-center">
              <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#fff' }}>Yükleniyor…</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          aria-label="Profil fotoğrafını değiştir"
          title="Profil fotoğrafını değiştir"
          className="absolute bottom-0 right-0 rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
          style={{
            width: badge,
            height: badge,
            background: '#e5599b',
            border: '3px solid #fff',
            boxShadow: '0px 1px 4px rgba(0,0,0,0.25)',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          <svg width={Math.round(badge * 0.5)} height={Math.round(badge * 0.5)} viewBox="0 0 24 24" fill="#fff">
            <path d="M20 4h-3.17L15 2H9L7.17 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm-8 13a5 5 0 110-10 5 5 0 010 10z" />
          </svg>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            pick(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <span className="max-w-[180px] text-center" style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 600, color: '#e5484d' }}>
          {error}
        </span>
      )}
    </div>
  );
};

export default AvatarUpload;
